// Soniox realtime provider (alternative). The key (temp or, when configured, the
// user's own "direct" key) is sent in the first WebSocket config message;
// WebSockets aren't subject to CORS. Each Soniox message mixes finalized and
// not-yet-final tokens, so we split one message into up to two ProviderResult
// batches — the final tokens (settled) and the interim tail. Speaker label lives
// at tokens[].speaker; timing at tokens[].start_ms/end_ms.

import type {
  ConnectOptions,
  ConnState,
  MintedToken,
  ProviderError,
  ProviderResult,
  ProviderToken,
  TranscriptionProvider,
  TranscriptionSession
} from '../types'

const WS_URL = 'wss://stt-rt.soniox.com/transcribe-websocket'
const TMP_KEY_URL = 'https://api.soniox.com/v1/auth/temporary-api-key'
const TOKEN_TTL_S = 3600

interface SonioxToken {
  text: string
  speaker?: string | number
  is_final?: boolean
  start_ms?: number
  end_ms?: number
}
interface SonioxMessage {
  tokens?: SonioxToken[]
  error_code?: string
  error_message?: string
}

function toToken(t: SonioxToken): ProviderToken {
  return {
    text: t.text,
    providerSpeaker: t.speaker !== undefined ? String(t.speaker) : '?',
    isFinal: Boolean(t.is_final),
    startMs: t.start_ms,
    endMs: t.end_ms
  }
}

export interface SonioxProviderOptions {
  /** Send the user's own key on the socket instead of minting a temp key. */
  directKey?: boolean
}

export function createSonioxProvider(
  cfg: SonioxProviderOptions = {}
): TranscriptionProvider {
  return {
    id: 'soniox',

    async mintToken(apiKey: string): Promise<MintedToken> {
      // Direct-key path: no mint, the long-lived key goes on the socket and
      // never expires (so the managed session runs no refresh timer).
      if (cfg.directKey) return { token: apiKey, expiresAt: Infinity }

      const res = await fetch(TMP_KEY_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          usage_type: 'transcribe_websocket',
          expires_in_seconds: TOKEN_TTL_S
        })
      })
      if (!res.ok) {
        throw new Error(`temp key failed: HTTP ${res.status} ${await res.text()}`)
      }
      const json = (await res.json()) as { api_key?: string }
      if (!json.api_key) throw new Error('temp key: no api_key in response')
      return { token: json.api_key, expiresAt: Date.now() + TOKEN_TTL_S * 1000 }
    },

    connect(opts: ConnectOptions): TranscriptionSession {
      const ws = new WebSocket(WS_URL)
      ws.binaryType = 'arraybuffer'

      let ready = false
      const queue: ArrayBuffer[] = []
      let onResult: (r: ProviderResult) => void = () => {}
      let onState: (s: ConnState, d?: string) => void = () => {}
      let onError: (e: ProviderError) => void = () => {}

      ws.onopen = () => {
        ws.send(
          JSON.stringify({
            api_key: opts.token,
            model: 'stt-rt-v5',
            audio_format: 'pcm_s16le',
            sample_rate: opts.sampleRate,
            num_channels: 1,
            enable_speaker_diarization: true,
            language_hints: ['en']
          })
        )
        ready = true
        onState('live', 'config sent')
        for (const buf of queue) ws.send(buf)
        queue.length = 0
      }

      ws.onmessage = (e: MessageEvent<string>) => {
        const msg = JSON.parse(e.data) as SonioxMessage
        if (msg.error_code) {
          const kind = /auth|key|forbidden/i.test(msg.error_code) ? 'auth' : 'unknown'
          onError({
            kind,
            message: `${msg.error_code}: ${msg.error_message ?? ''}`,
            retryable: kind !== 'auth'
          })
          onState('error', msg.error_code)
          return
        }
        // Split into settled finals and the volatile interim tail, emitting the
        // final batch first so the segmenter promotes before re-laying interim.
        const finals: ProviderToken[] = []
        const interim: ProviderToken[] = []
        for (const t of msg.tokens ?? []) {
          if (!t.text) continue
          ;(t.is_final ? finals : interim).push(toToken(t))
        }
        if (finals.length) onResult({ tokens: finals, isFinal: true })
        // Always publish the interim view (even when empty) so a now-empty tail
        // clears the previous partial.
        onResult({ tokens: interim, isFinal: false })
      }

      ws.onerror = () =>
        onError({ kind: 'network', message: 'websocket error', retryable: true })
      ws.onclose = (e) => {
        if (ready) onState('closed', `socket closed (${e.code})`)
        else onState('error', `closed before config (${e.code})`)
      }

      return {
        sendAudio(pcm) {
          if (!ready) {
            queue.push(pcm)
            return
          }
          if (ws.readyState === WebSocket.OPEN) ws.send(pcm)
        },
        onResult: (cb) => {
          onResult = cb
        },
        onState: (cb) => {
          onState = cb
        },
        onError: (cb) => {
          onError = cb
        },
        close() {
          return new Promise<void>((resolve) => {
            if (ws.readyState === WebSocket.OPEN) {
              onState('closing', 'sending end-of-audio')
              // An empty binary frame signals end-of-audio; the server finalizes.
              ws.send(new ArrayBuffer(0))
            }
            ws.onclose = () => resolve()
            ws.close()
          })
        }
      }
    }
  }
}
